import {
    useState,
    useEffect,
    useMemo,
    useCallback,
    useRef,
    ComponentProps,
} from 'react'
import { Brush } from 'recharts'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
    calculateGranularityDates,
    dateToIndex,
    indexToDate,
} from './calculateGranularityIndices'
import { GranularityType, IntervalDataGroupingParam } from '@/hooks'

dayjs.extend(utc)

/**
 * Detects the data grouping structure from processedData.
 * - Returns 'aggregate' if data has 'value' property (aggregate mode)
 * - Returns 'compare' if data has numeric keys (meterIds) instead of 'value' (compare mode)
 */
const detectDataGroupingStructure = (
    processedData: (Record<string, unknown> & { date: string })[]
): IntervalDataGroupingParam => {
    if (processedData.length === 0) {
        return 'aggregate' // Default to aggregate
    }
    const firstItem = processedData[0]
    // Check if it has a 'value' property (aggregate mode)
    if ('value' in firstItem && firstItem.value !== undefined) {
        return 'aggregate'
    }
    // Check if it has numeric keys (compare mode)
    const hasNumericKeys = Object.keys(firstItem).some(
        (key) => key !== 'date' && key !== 'timestamp' && !isNaN(Number(key))
    )
    return hasNumericKeys ? 'compare' : 'aggregate'
}

export const useBrushProps = (
    processedData: (Record<string, unknown> & { date: string })[],
    granularity?: GranularityType,
    onBrushManualChange?: () => void
) => {
    const dataLength = processedData.length

    // Add timestamps to processedData for time-scale charts
    const dataWithTimestamps = useMemo(() => {
        return processedData.map((item) => ({
            ...item,
            timestamp: dayjs.utc(item.date).valueOf(),
        }))
    }, [processedData])

    const [brushStartDate, setBrushStartDate] = useState<string | undefined>(
        undefined
    )
    const [brushEndDate, setBrushEndDate] = useState<string | undefined>(
        undefined
    )
    // Store the last indices from Brush component to preserve out-of-bounds values
    const [lastBrushStartIndex, setLastBrushStartIndex] = useState<
        number | undefined
    >(undefined)
    const [lastBrushEndIndex, setLastBrushEndIndex] = useState<
        number | undefined
    >(undefined)
    const previousGranularityRef = useRef<GranularityType | undefined>(
        granularity
    )
    const previousDataLengthRef = useRef<number>(dataLength)
    const previousProcessedDataRef =
        useRef<(Record<string, unknown> & { date: string })[]>(processedData)
    // Track previous data grouping structure to detect structural changes
    const previousDataGroupingRef = useRef<'aggregate' | 'compare'>(
        detectDataGroupingStructure(processedData)
    )
    // Track if user has manually interacted with the brush
    const hasUserInteractedRef = useRef<boolean>(false)
    // Track if we've initialized (to detect initial load)
    const isInitializedRef = useRef<boolean>(false)
    // Track current brushEndDate in a ref to avoid dependency issues
    const brushEndDateRef = useRef<string | undefined>(brushEndDate)
    // Track previous brush span to detect resize vs move
    const previousBrushSpanRef = useRef<number | undefined>(undefined)
    // Store visual brush indices during interaction (to avoid rerenders)
    const visualBrushStartIndexRef = useRef<number | undefined>(undefined)
    const visualBrushEndIndexRef = useRef<number | undefined>(undefined)

    // Keep ref in sync with state
    useEffect(() => {
        brushEndDateRef.current = brushEndDate
    }, [brushEndDate])

    // Helper function to clear all brush indices
    const clearAllBrushIndices = useCallback(() => {
        setLastBrushStartIndex(undefined)
        setLastBrushEndIndex(undefined)
        setCommittedStartIndex(undefined)
        setCommittedEndIndex(undefined)
    }, [])

    // Helper function to reset all brush state
    const resetBrushState = useCallback(
        (resetUserInteraction: boolean = false) => {
            setBrushStartDate(undefined)
            setBrushEndDate(undefined)
            clearAllBrushIndices()
            if (resetUserInteraction) {
                hasUserInteractedRef.current = false
            }
            previousBrushSpanRef.current = undefined
        },
        [clearAllBrushIndices]
    )

    // Helper function to safely get last data point date
    const getLastDataPointDate = useCallback((): string | undefined => {
        return processedData.length > 0
            ? processedData[processedData.length - 1]?.date
            : undefined
    }, [processedData])

    // Helper function to validate brush dates
    const validateBrushDates = useCallback(
        (startDate: string, endDate: string, dataLength: number): boolean => {
            if (dataLength === 0) {
                return false
            }
            try {
                const startIdx = dateToIndex(startDate, processedData)
                const endIdx = dateToIndex(endDate, processedData)
                // Ensure meaningful span: startIdx must be strictly less than endIdx
                const isValid =
                    startIdx >= 0 &&
                    endIdx >= 0 &&
                    startIdx < dataLength &&
                    endIdx < dataLength &&
                    startIdx < endIdx && // Fixed: changed from <= to <
                    dayjs.utc(startDate).isBefore(dayjs.utc(endDate))
                return isValid
            } catch {
                return false
            }
        },
        [processedData]
    )

    // Helper function to apply granularity dates
    const applyGranularityDates = useCallback(
        (
            dates: { startDate: string; endDate: string } | null,
            resetUserInteraction: boolean = false
        ) => {
            if (dates) {
                setBrushStartDate(dates.startDate)
                setBrushEndDate(dates.endDate)
                clearAllBrushIndices()
                if (resetUserInteraction) {
                    hasUserInteractedRef.current = false
                }
                previousBrushSpanRef.current = undefined
            } else {
                resetBrushState(resetUserInteraction)
            }
        },
        [clearAllBrushIndices, resetBrushState]
    )

    useEffect(() => {
        // Detect actual data changes
        const isActualDataChange =
            previousDataLengthRef.current !== dataLength ||
            previousProcessedDataRef.current !== processedData

        // Detect structural data changes (aggregate ↔ compare mode switch)
        const currentDataGrouping = detectDataGroupingStructure(processedData)
        const isStructuralChange =
            previousDataGroupingRef.current !== currentDataGrouping &&
            isInitializedRef.current

        // Detect granularity change: any change in granularity value (excluding initial load)
        // This includes: undefined -> value, value -> value, value -> undefined
        const isGranularityChange =
            previousGranularityRef.current !== granularity &&
            isInitializedRef.current

        // When granularity changes from a value to undefined (user cleared it via manual brush),
        // and user has already interacted, don't recalculate - let their manual selection stand
        const isClearingGranularity =
            previousGranularityRef.current !== undefined &&
            granularity === undefined &&
            hasUserInteractedRef.current

        const isInitialLoad = !isInitializedRef.current

        if (dataLength > 0) {
            // If structural change detected (mode switch), force recalculation from granularity
            // This ensures the brush properly initializes when switching between aggregate and compare modes
            if (isStructuralChange) {
                const lastDataPointDate = getLastDataPointDate()
                const dates = calculateGranularityDates(
                    processedData,
                    granularity,
                    lastDataPointDate
                )
                applyGranularityDates(dates, true)
            } else if (isGranularityChange && !isClearingGranularity) {
                // If granularity changed, recalculate brush range (prioritize over data changes)
                // But only if user hasn't manually interacted, or if they have, only update start
                // Skip recalculation if user is clearing granularity via manual brush adjustment
                // Use current brushEndDate if available (should be at dataMax from initial load),
                // otherwise fall back to last data point's date to prevent empty intervals
                const endDateForCalculation =
                    brushEndDateRef.current || getLastDataPointDate()
                const dates = calculateGranularityDates(
                    processedData,
                    granularity,
                    endDateForCalculation
                )
                if (dates) {
                    setBrushStartDate(dates.startDate)
                    clearAllBrushIndices()
                    // Reset brush span tracking when granularity changes
                    previousBrushSpanRef.current = undefined

                    // Always update end date when granularity changes
                    // The calculateGranularityDates function will adjust it if needed to show full period
                    const currentEndDate = endDateForCalculation
                        ? dayjs.utc(endDateForCalculation)
                        : null
                    const newEndDate = dayjs.utc(dates.endDate)

                    // Update end date if:
                    // 1. User hasn't interacted (always update)
                    // 2. User has interacted but end date was adjusted (to show full granularity period)
                    if (
                        !hasUserInteractedRef.current ||
                        (currentEndDate &&
                            newEndDate.isValid() &&
                            !newEndDate.isSame(currentEndDate, 'minute'))
                    ) {
                        setBrushEndDate(dates.endDate)
                    }
                    // If user has interacted and end date wasn't adjusted, keep their selection
                    // but we've already cleared the stored index above so dates will be used
                }
            } else if (isActualDataChange || isInitialLoad) {
                // On actual data change or initial load, recalculate indices from stored dates (from granularity or custom adjustment)
                // On initial load, calculate dates from granularity
                if (isActualDataChange && brushStartDate && brushEndDate) {
                    // Data changed - verify that dates can be converted to valid indices in the data
                    const indicesValid = validateBrushDates(
                        brushStartDate,
                        brushEndDate,
                        dataLength
                    )

                    if (indicesValid) {
                        // Stored dates are still valid - preserve them and recalculate indices in the new data
                        // Clear stored indices so they're recalculated from dates in the new data
                        clearAllBrushIndices()
                        // Keep the stored dates - they will be used to recalculate indices in the new data
                    } else {
                        // Stored dates are invalid in new data - recalculate from granularity
                        const lastDataPointDate = getLastDataPointDate()
                        const dates = calculateGranularityDates(
                            processedData,
                            granularity,
                            lastDataPointDate
                        )
                        applyGranularityDates(dates, true)
                    }
                } else {
                    // Initial load or data change without stored dates - recalculate dates from granularity
                    const lastDataPointDate = getLastDataPointDate()
                    const dates = calculateGranularityDates(
                        processedData,
                        granularity,
                        lastDataPointDate
                    )
                    applyGranularityDates(dates, isInitialLoad)
                }
            }
            // If nothing changed and we're already initialized, do nothing
        } else {
            // No data available - reset all brush state
            resetBrushState(true)
        }

        previousGranularityRef.current = granularity
        previousDataLengthRef.current = dataLength
        previousProcessedDataRef.current = processedData
        previousDataGroupingRef.current = currentDataGrouping
        isInitializedRef.current = true
    }, [
        dataLength,
        granularity,
        processedData,
        brushStartDate,
        brushEndDate,
        dataWithTimestamps,
        applyGranularityDates,
        clearAllBrushIndices,
        getLastDataPointDate,
        resetBrushState,
        validateBrushDates,
    ])

    // Refs for tracking drag state and pending changes
    const isDraggingRef = useRef(false)
    const pendingBrushDataRef = useRef<{
        startIndex?: number
        endIndex?: number
    } | null>(null)
    const dragEndTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Committed indices - only update on drag end, used for filteredData/brushTimestamps
    const [committedStartIndex, setCommittedStartIndex] = useState<
        number | undefined
    >(undefined)
    const [committedEndIndex, setCommittedEndIndex] = useState<
        number | undefined
    >(undefined)

    // Commit pending brush changes (called on drag end)
    const commitBrushChange = useCallback(() => {
        const brushData = pendingBrushDataRef.current
        if (!brushData) return

        // Mark that user has manually interacted with the brush
        hasUserInteractedRef.current = true

        // Get indices from refs (updated during interaction)
        const currentStartIndex =
            visualBrushStartIndexRef.current ?? lastBrushStartIndex ?? 0
        const currentEndIndex =
            visualBrushEndIndexRef.current ?? lastBrushEndIndex ?? 0
        const currentSpan = currentEndIndex - currentStartIndex

        // Only clear granularity if the brush size changed (resize), not if it's just moved (same size)
        const isResize =
            previousBrushSpanRef.current !== undefined &&
            previousBrushSpanRef.current !== currentSpan

        if (isResize && onBrushManualChange) {
            onBrushManualChange()
        }

        // Update previous span for next comparison
        previousBrushSpanRef.current = currentSpan

        // Commit the indices and dates from refs to state (triggers rerender)
        if (visualBrushStartIndexRef.current !== undefined) {
            const startIdx = visualBrushStartIndexRef.current
            setLastBrushStartIndex(startIdx)
            setCommittedStartIndex(startIdx)
            const startDate = indexToDate(startIdx, processedData)
            if (startDate !== undefined) {
                setBrushStartDate(startDate)
            }
        }
        if (visualBrushEndIndexRef.current !== undefined) {
            const endIdx = visualBrushEndIndexRef.current
            setLastBrushEndIndex(endIdx)
            setCommittedEndIndex(endIdx)
            const endDate = indexToDate(endIdx, processedData)
            if (endDate !== undefined) {
                setBrushEndDate(endDate)
            }
        }

        isDraggingRef.current = false
        pendingBrushDataRef.current = null
        // Clear visual refs after committing
        visualBrushStartIndexRef.current = undefined
        visualBrushEndIndexRef.current = undefined
    }, [
        processedData,
        lastBrushStartIndex,
        lastBrushEndIndex,
        onBrushManualChange,
    ])

    const handleBrushChange = useCallback(
        (brushData: { startIndex?: number; endIndex?: number } | null) => {
            if (brushData) {
                // Store pending data
                pendingBrushDataRef.current = brushData
                isDraggingRef.current = true

                // Update visual brush indices in refs only (no state updates during interaction)
                // This prevents re-renders and main chart updates until interaction ends
                if (brushData.startIndex !== undefined) {
                    visualBrushStartIndexRef.current = brushData.startIndex
                }
                if (brushData.endIndex !== undefined) {
                    visualBrushEndIndexRef.current = brushData.endIndex
                }

                // Debounce: commit changes after 150ms of inactivity (drag end)
                if (dragEndTimeoutRef.current) {
                    clearTimeout(dragEndTimeoutRef.current)
                }
                dragEndTimeoutRef.current = setTimeout(() => {
                    commitBrushChange()
                }, 150)
            }
        },
        [commitBrushChange]
    )

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (dragEndTimeoutRef.current) {
                clearTimeout(dragEndTimeoutRef.current)
            }
        }
    }, [])

    // Convert dates to indices for the Brush component
    // Use stored indices if available (to preserve out-of-bounds values), otherwise convert from dates
    // Indices are relative to processedData
    // During interaction, read from refs; Brush component handles its own visual updates via onChange
    const brushStartIndex = useMemo(() => {
        // During interaction, return ref value (Brush component uses onChange for updates)
        if (
            isDraggingRef.current &&
            visualBrushStartIndexRef.current !== undefined
        ) {
            return visualBrushStartIndexRef.current
        }
        // After interaction, use state
        if (lastBrushStartIndex !== undefined) {
            return lastBrushStartIndex
        }
        if (brushStartDate === undefined) {
            return undefined
        }
        return dateToIndex(brushStartDate, processedData)
    }, [lastBrushStartIndex, brushStartDate, processedData])

    const brushEndIndex = useMemo(() => {
        // During interaction, return ref value (Brush component uses onChange for updates)
        if (
            isDraggingRef.current &&
            visualBrushEndIndexRef.current !== undefined
        ) {
            return visualBrushEndIndexRef.current
        }
        // After interaction, use state
        if (lastBrushEndIndex !== undefined) {
            return lastBrushEndIndex
        }
        if (brushEndDate === undefined) {
            return undefined
        }
        return dateToIndex(brushEndDate, processedData)
    }, [lastBrushEndIndex, brushEndDate, processedData])

    const brushProps: ComponentProps<typeof Brush> = useMemo(
        () => ({
            dataKey: 'date',
            startIndex: brushStartIndex,
            endIndex: brushEndIndex,
            onChange: handleBrushChange,
            height: 50,
            tickFormatter: () => '',
        }),
        [brushStartIndex, brushEndIndex, handleBrushChange]
    )

    // Compute the brush selection timestamps for the main chart domain
    // Uses committed indices so the domain only updates on drag end
    // Look up timestamps directly from dataWithTimestamps[index].timestamp to get exact values
    const brushTimestamps = useMemo((): {
        startTimestamp: number | undefined
        endTimestamp: number | undefined
    } => {
        if (dataWithTimestamps.length === 0) {
            return { startTimestamp: undefined, endTimestamp: undefined }
        }

        // Use committed indices, fall back to brushStartIndex/brushEndIndex for initial load
        const startIdx = committedStartIndex ?? brushStartIndex ?? 0
        const endIdx =
            committedEndIndex ?? brushEndIndex ?? dataWithTimestamps.length - 1

        const clampedStart = Math.max(
            0,
            Math.min(startIdx, dataWithTimestamps.length - 1)
        )
        const clampedEnd = Math.max(
            0,
            Math.min(endIdx, dataWithTimestamps.length - 1)
        )

        // Look up timestamps directly from dataWithTimestamps to get exact values at those indices
        return {
            startTimestamp: dataWithTimestamps[clampedStart].timestamp,
            endTimestamp: dataWithTimestamps[clampedEnd].timestamp,
        }
    }, [
        committedStartIndex,
        committedEndIndex,
        brushStartIndex,
        brushEndIndex,
        dataWithTimestamps,
    ])

    // Filter data by brush timestamps
    const filteredData = useMemo(() => {
        const { startTimestamp, endTimestamp } = brushTimestamps
        if (startTimestamp === undefined || endTimestamp === undefined) {
            return dataWithTimestamps
        }
        // Handle case where startIndex > endIndex by swapping timestamps
        const minTimestamp = Math.min(startTimestamp, endTimestamp)
        const maxTimestamp = Math.max(startTimestamp, endTimestamp)
        return dataWithTimestamps.filter(
            (item) =>
                item.timestamp >= minTimestamp && item.timestamp <= maxTimestamp
        )
    }, [dataWithTimestamps, brushTimestamps])

    return { brushProps, dataWithTimestamps, brushTimestamps, filteredData }
}
